import "./styles.scss";

import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { Accordion, Alert, Badge, Carousel, Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import StoreImage from "../../../components/StoreImage";
import { useGetGameImage } from "../../../hooks/getGameImage";
import { GameI, UserI } from "../../../types";
import { Pencil, PersonFill, StarFill } from "react-bootstrap-icons";
import LightBox from "../../../components/LightBox";
import { NavLink, useParams } from "react-router-dom";
import { useGetDbData } from "../../../hooks/getDbData";
import { objectToArray } from "../../../functions";
import moment from "moment";
import "moment/locale/sk";
import "moment/locale/en-gb";
import { GlobalContext } from "../../../store";
import { Helmet } from "react-helmet";
import { siteTitle } from "../../../settings";

export const ShowGame: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { gameUrl } = useParams();
    const store = useContext(GlobalContext);

    moment.locale(i18n.language === "sk" ? "sk" : "en-gb");

    const [game, setGame] = useState<GameI | null>();
    const [showLightBox, setShowLightBox] = useState<boolean>(false);
    const [lightBoxJSX, setLightBoxJSX] = useState<React.ReactNode>();
    const [lightBoxTitle, setLightBoxTitle] = useState<string>();
    const [author, setAuthor] = useState<UserI>();
    const [gameFound, setGameFound] = useState<boolean | undefined>(undefined);

    const { sendRequest: fetchGameImage } = useGetGameImage();
    const { sendRequest: fetchDbData } = useGetDbData();

    const showLightBoxHandler = (image: { image: string; platform: string; alt?: string }): void => {
        setLightBoxJSX(
            <StoreImage src={image.image} platform={image.platform} alt={image.alt} fluid className={"w-100"} />
        );
        setLightBoxTitle(image.alt);
        setShowLightBox(true);
    };

    useEffect(() => {
        fetchDbData<GameI>({ table: "game", orderBy: "url", where: gameUrl, isExact: false }, (dbData) => {
            if (dbData === null) {
                setGameFound(false);
            } else {
                console.log(dbData);

                setGameFound(true);
                setGame(objectToArray<GameI>(dbData)[0]);
            }
        });
        // eslint-disable-next-line
    }, [gameUrl]);

    useEffect(() => {
        if (game) {
            fetchDbData<UserI[]>({ table: "user", orderBy: "email", where: game?.author }, (dbData) => {
                setAuthor(dbData[0]);
            });
        }

        // eslint-disable-next-line
    }, [game, fetchGameImage]);

    return (
        <>
            {typeof gameFound === "undefined" ? (
                <Alert variant={"primary"}>
                    <Spinner animation={"border"} size={"sm"} /> {t("Loading game")}...
                </Alert>
            ) : (
                <>
                    {gameFound && game ? (
                        <>
                            <Helmet>
                                <title>
                                    {game.title} | {siteTitle}
                                </title>
                                {game.metaDesc ? (
                                    <meta name="description" content={game.metaDesc} data-page-subject="true" />
                                ) : null}
                                {game.metaKeys ? (
                                    <meta name="keywords" content={game.metaKeys} data-page-subject="true" />
                                ) : null}
                            </Helmet>

                            <div className={"poster mb-3"}>
                                {game.picture.inGame && game.picture.inGameNr && game.picture.inGameNr > 0 ? (
                                    <Carousel className={"mb-3"} slide>
                                        {game.picture.intro ? (
                                            <Carousel.Item>
                                                <StoreImage
                                                    src={game.picture.intro}
                                                    platform={game.platform}
                                                    alt={`${game.title} (Intro)`}
                                                    onClick={showLightBoxHandler}
                                                    className={"d-block w-100"}
                                                />
                                            </Carousel.Item>
                                        ) : null}

                                        {game.picture.poster ? (
                                            <Carousel.Item>
                                                <StoreImage
                                                    src={game.picture.poster}
                                                    platform={game.platform}
                                                    alt={`${game.title} (${String(t("Poster"))})`}
                                                    onClick={showLightBoxHandler}
                                                    className={"d-block w-100"}
                                                />
                                            </Carousel.Item>
                                        ) : null}

                                        {game.picture.menu ? (
                                            <Carousel.Item>
                                                <StoreImage
                                                    src={game.picture.menu}
                                                    platform={game.platform}
                                                    alt={`${game.title} (Menu)`}
                                                    onClick={showLightBoxHandler}
                                                    className={"d-block w-100"}
                                                />
                                            </Carousel.Item>
                                        ) : null}

                                        {[...Array(game.picture.inGameNr)].map((x, i) => (
                                            <Carousel.Item
                                                key={`ingame-image-${game.picture.inGame.replace(
                                                    "[N]",
                                                    String(i + 1)
                                                )}`}
                                            >
                                                <StoreImage
                                                    src={`${game.picture.inGame.replace("[N]", String(i + 1))}.${game.jpgImages ? "jpg" : "png"
                                                        }`}
                                                    platform={game.platform}
                                                    alt={`${game?.title} (${String(t("In-game Picture"))})`}
                                                    onClick={showLightBoxHandler}
                                                    className={"d-block w-100"}
                                                />
                                            </Carousel.Item>
                                        ))}
                                    </Carousel>
                                ) : null}

                                {game.rating.total ? (
                                    <Badge bg={"primary"} className={"card-image-rating"}>
                                        <StarFill /> {game.rating.total} / 10
                                    </Badge>
                                ) : null}

                                {store.loggedUser?.role === "admin" ? (
                                    <NavLink
                                        to={`/administration/game/edit/${game.url}`}
                                        className={"btn btn-secondary btn-edit-game"}
                                    >
                                        <Pencil /> {t("Edit")}
                                    </NavLink>
                                ) : null}
                            </div>

                            <h1 className={"text-primary text-center mb-3"}>{game.title}</h1>

                            {game.date.testing ? (
                                <p className={"text-end"}>
                                    <strong>{t("Testing date")}: </strong>
                                    {moment(game.date.testing).format("D. MMMM. YYYY")} (
                                    {moment(game.date.testing, "YYYYMMDD").fromNow()})
                                </p>
                            ) : null}

                            <p className={"break-db-text"}>{game.description}</p>

                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th colSpan={2}>{t("Author(s)")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {game.publisher ? (
                                        <tr>
                                            <td>{t("Publisher")}:</td>
                                            <td>{game.publisher}</td>
                                        </tr>
                                    ) : null}

                                    {game.date.published ? (
                                        <tr>
                                            <td>{t("Released")}:</td>
                                            <td>
                                                {moment(game.date.published).format("YYYY")} (
                                                {moment(game.date.published, "YYYYMMDD").fromNow()})
                                            </td>
                                        </tr>
                                    ) : null}

                                    {game.intro ? (
                                        <tr>
                                            <td>Intro:</td>
                                            <td>{game.intro}</td>
                                        </tr>
                                    ) : null}

                                    {game.date.cracked ? (
                                        <tr>
                                            <td>{t("Date Cracked")}:</td>
                                            <td>
                                                {moment(game.date.cracked).format("YYYY")} (
                                                {moment(game.date.cracked, "YYYYMMDD").fromNow()})
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>

                                <thead>
                                    <tr>
                                        <th colSpan={2}>{t("Information")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{t("Platform")}:</td>
                                        <td>
                                            <Badge bg={"primary"}>{game.platform}</Badge>
                                        </td>
                                    </tr>

                                    {game.control ? (
                                        <tr>
                                            <td>{t("Controls")}:</td>
                                            <td>
                                                {game.control.map((control) => {
                                                    return (
                                                        <Badge bg={"primary"} key={`game-control-${control}`}>
                                                            {control}
                                                        </Badge>
                                                    );
                                                })}
                                            </td>
                                        </tr>
                                    ) : null}

                                    {game.players && game.players > 0 ? (
                                        <tr>
                                            <td>{t("Number of Players")}:</td>
                                            <td>
                                                {game.players <= 4 ? (
                                                    [...Array(game.players)].map((player, index) => {
                                                        return <PersonFill key={`player-number-${index}`} />;
                                                    })
                                                ) : (
                                                    <>
                                                        <PersonFill /> {game.players}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ) : null}

                                    {game.genre ? (
                                        <tr>
                                            <td>{t("Genre")}:</td>
                                            <td>
                                                {game.genre.map((genre) => {
                                                    return (
                                                        <Badge
                                                            key={`game-genre-${genre}`}
                                                            bg={"primary"}
                                                            className={"genre"}
                                                        >
                                                            <NavLink
                                                                to={`/games/genre/${genre}`}
                                                                className={"genre-link"}
                                                            >
                                                                {t(genre)}
                                                            </NavLink>
                                                        </Badge>
                                                    );
                                                })}
                                            </td>
                                        </tr>
                                    ) : null}

                                    {game.format ? (
                                        <tr>
                                            <td>{t("File Format")}:</td>
                                            <td>
                                                <Badge bg={"primary"}>{game.format}</Badge>
                                            </td>
                                        </tr>
                                    ) : null}

                                    <tr>
                                        <td>Trainer:</td>
                                        <td>
                                            <Form.Check type={"switch"} checked={game.trainer} readOnly />
                                        </td>
                                    </tr>
                                </tbody>

                                <thead>
                                    <tr>
                                        <th colSpan={2}>{t("Rating")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {game.rating.graphic ? (
                                        <tr>
                                            <td>{t("Graphics")}:</td>
                                            <td>{game.rating.graphic} / 10</td>
                                        </tr>
                                    ) : null}

                                    {game.rating.music ? (
                                        <tr>
                                            <td>{t("Music")}:</td>
                                            <td>{game.rating.music} / 10</td>
                                        </tr>
                                    ) : null}

                                    {game.rating.sound ? (
                                        <tr>
                                            <td>{t("Sound")}:</td>
                                            <td>{game.rating.sound} / 10</td>
                                        </tr>
                                    ) : null}

                                    {game.rating.intro ? (
                                        <tr>
                                            <td>Intro:</td>
                                            <td>{game.rating.intro} / 10</td>
                                        </tr>
                                    ) : null}

                                    {game.rating.total ? (
                                        <tr>
                                            <td>{t("Total")}:</td>
                                            <td>
                                                <Badge bg={"primary"}>{game.rating.total} / 10</Badge>
                                            </td>
                                        </tr>
                                    ) : null}

                                    {game.externalLink ? (
                                        <tr>
                                            <td>{t("External review")}:</td>
                                            <td>
                                                <a
                                                    className={"external-link"}
                                                    href={game.externalLink}
                                                    target={"_blank"}
                                                    rel="noreferrer"
                                                >
                                                    {game.externalLink}
                                                </a>
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>

                                {game.picture.cartridge || game.picture.docu || game.picture.map || game.manual || game.instructions || game.cheats ? (
                                    <>
                                        <thead>
                                            <tr>
                                                <th colSpan={2}>{t("Other")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td colSpan={2}>
                                                    <Accordion>
                                                        {game.picture.cartridge ? (
                                                            <Accordion.Item eventKey="cover">
                                                                <Accordion.Header>{t("Cover")}</Accordion.Header>
                                                                <Accordion.Body>
                                                                    <StoreImage
                                                                        src={game.picture.cartridge}
                                                                        platform={game.platform}
                                                                        alt={`${game.title} (${String(t("Cartridge"))})`}
                                                                        onClick={showLightBoxHandler}
                                                                        className={"thumbnail"}
                                                                    />
                                                                </Accordion.Body>
                                                            </Accordion.Item>

                                                        ) : null}

                                                        {game.picture.docu ? (
                                                            <Accordion.Item eventKey="docs">
                                                                <Accordion.Header>{t("Documentation")}</Accordion.Header>
                                                                <Accordion.Body>
                                                                    <StoreImage
                                                                        src={game.picture.docu}
                                                                        platform={game.platform}
                                                                        alt={`${game.title} (${String(t("Documentation"))})`}
                                                                        onClick={showLightBoxHandler}
                                                                        className={"thumbnail"}
                                                                    />
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        ) : null}

                                                        {game.picture.map ? (
                                                            <Accordion.Item eventKey="map">
                                                                <Accordion.Header>{t("Map")}</Accordion.Header>
                                                                <Accordion.Body>
                                                                    <StoreImage
                                                                        src={game.picture.map}
                                                                        platform={game.platform}
                                                                        alt={`${game.title} (${String(t("Map"))})`}
                                                                        onClick={showLightBoxHandler}
                                                                        className={"thumbnail"}
                                                                    />
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        ) : null}

                                                        {game.manual ? (
                                                            <Accordion.Item eventKey="manual">
                                                                <Accordion.Header>{t("Manual")}</Accordion.Header>
                                                                <Accordion.Body>
                                                                    <p className={"monospace break-db-text"}>{game.manual}</p>
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        ) : null}

                                                        {game.instructions ? (
                                                            <Accordion.Item eventKey="instructions">
                                                                <Accordion.Header>{t("Instructions")}</Accordion.Header>
                                                                <Accordion.Body>
                                                                    <p className={"monospace break-db-text"}>{game.instructions}</p>
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        ) : null}

                                                        {game.cheats ? (
                                                            <Accordion.Item eventKey="cheats">
                                                                <Accordion.Header>{t("Cheats")}</Accordion.Header>
                                                                <Accordion.Body>
                                                                    <p className={"monospace break-db-text"}>{game.cheats}</p>
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        ) : null}
                                                    </Accordion>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </>) : null}

                            </Table>

                            <p className={"text-center"}>
                                {t("Added by")}{" "}
                                <Badge bg={"primary"}>{author?.nickname ?? author?.name ?? author?.email}</Badge>,{" "}
                                {moment(game.date.added).format("D. MMMM. YYYY")} (
                                {moment(game.date.added, "YYYYMMDD HH:mm:ss").fromNow()})
                            </p>
                        </>
                    ) : (
                        <Alert variant={"danger"}>
                            {t("Game not found! Please, go to ")}
                            <NavLink to={"/games"}>{t("Games")}</NavLink>
                            {t(" section and try to search again")}...
                        </Alert>
                    )}
                </>
            )}

            {showLightBox ? (
                <LightBox show={showLightBox} onClose={() => setShowLightBox(false)} title={lightBoxTitle}>
                    {lightBoxJSX}
                </LightBox>
            ) : null}
        </>
    );
};
